import { useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '@/store/authStore';
import { authService } from '@/services/authService';

// Refresh token 60 seconds before expiry
const REFRESH_BUFFER_SECONDS = 60;
// Maximum retry attempts for network errors
const MAX_REFRESH_RETRIES = 3;
// Delay between retries (exponential backoff base)
const RETRY_BASE_DELAY_MS = 2000;

export const useAuth = () => {
  const {
    token,
    refreshToken,
    isAuthenticated,
    isInitialized,
    setTokens,
    setInitialized,
    logout: storeLogout
  } = useAuthStore();

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshInProgressRef = useRef<Promise<void> | null>(null);
  const retryCountRef = useRef(0);

  // Perform token refresh with retry logic
  const performRefresh = useCallback(async (): Promise<boolean> => {
    // Get fresh token data (not stale closure)
    const stored = authService.getStoredTokens();
    if (!stored?.refreshToken) {
      console.log('[Auth] No refresh token available');
      return false;
    }

    try {
      console.log('[Auth] Refreshing token...');
      const tokens = await authService.refreshToken(stored.refreshToken);
      setTokens(tokens.accessToken, tokens.refreshToken);
      retryCountRef.current = 0; // Reset retry count on success
      return true;
    } catch (error: any) {
      // Check if it's a network error (not an auth error)
      const isNetworkError = !error.message?.includes('expired') &&
        !error.message?.includes('invalid') &&
        (error.name === 'TypeError' || error.message?.includes('fetch') || error.message?.includes('network'));

      if (isNetworkError && retryCountRef.current < MAX_REFRESH_RETRIES) {
        retryCountRef.current++;
        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retryCountRef.current - 1);
        console.log(`[Auth] Network error, retry ${retryCountRef.current}/${MAX_REFRESH_RETRIES} in ${delay}ms`);

        await new Promise(resolve => setTimeout(resolve, delay));
        return performRefresh(); // Retry
      }

      console.error('[Auth] Token refresh failed:', error);
      return false;
    }
  }, [setTokens]);

  // Schedule automatic token refresh
  const scheduleTokenRefresh = useCallback(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const stored = authService.getStoredTokens();
    if (!stored?.refreshToken) return;

    const now = Date.now();
    const expiresAt = stored.expiry;
    const refreshAt = expiresAt - (REFRESH_BUFFER_SECONDS * 1000);
    const delay = Math.max(refreshAt - now, 10000); // At least 10 seconds

    console.log(`[Auth] Scheduling token refresh in ${Math.round(delay / 1000)}s`);

    refreshTimerRef.current = setTimeout(async () => {
      const success = await performRefresh();
      if (success) {
        scheduleTokenRefresh(); // Schedule next refresh
      } else {
        // Session expired, will show login
        storeLogout();
        authService.clearTokens();
      }
    }, delay);
  }, [performRefresh, storeLogout]);

  // Check token on visibility change (tab becomes visible)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        console.log('[Auth] Tab became visible, checking token...');

        // Check if token is expired or about to expire
        if (authService.isTokenExpired(REFRESH_BUFFER_SECONDS)) {
          console.log('[Auth] Token expired while tab was hidden, refreshing...');
          const success = await performRefresh();
          if (success) {
            scheduleTokenRefresh();
          } else {
            storeLogout();
            authService.clearTokens();
          }
        } else {
          // Token is still valid, reschedule refresh (timer might have been throttled)
          scheduleTokenRefresh();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, performRefresh, scheduleTokenRefresh, storeLogout]);

  // Initialize authentication on mount
  useEffect(() => {
    const initAuth = async () => {
      if (isInitialized) return;

      console.log('[Auth] Initializing authentication...');
      try {
        const tokens = await authService.initializeAuth();

        if (tokens) {
          console.log('[Auth] Found valid tokens');
          setTokens(tokens.accessToken, tokens.refreshToken);
          scheduleTokenRefresh();
        } else {
          console.log('[Auth] No valid tokens found');
        }
      } catch (error) {
        console.error('[Auth] Initialization error:', error);
      } finally {
        setInitialized(true);
      }
    };

    initAuth();

    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [isInitialized, setTokens, setInitialized, scheduleTokenRefresh]);

  // Login function
  const login = useCallback(async (username: string, password: string, otp?: string) => {
    const tokens = await authService.login({ username, password, otp });
    setTokens(tokens.accessToken, tokens.refreshToken);
    scheduleTokenRefresh();
    return tokens;
  }, [setTokens, scheduleTokenRefresh]);

  // Logout function
  const logout = useCallback(async () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    await authService.logout();
    storeLogout();
  }, [storeLogout]);

  // Manual token refresh (for retrying failed requests)
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const stored = authService.getStoredTokens();
    if (!stored?.refreshToken) return null;

    // Prevent concurrent refresh attempts
    if (refreshInProgressRef.current) {
      await refreshInProgressRef.current;
      return authService.getStoredTokens()?.accessToken || null;
    }

    refreshInProgressRef.current = (async () => {
      try {
        const tokens = await authService.refreshToken(stored.refreshToken);
        setTokens(tokens.accessToken, tokens.refreshToken);
        scheduleTokenRefresh();
      } catch (error) {
        console.error('[Auth] Manual refresh failed:', error);
        storeLogout();
        authService.clearTokens();
        throw error;
      } finally {
        refreshInProgressRef.current = null;
      }
    })();

    await refreshInProgressRef.current;
    return authService.getStoredTokens()?.accessToken || null;
  }, [setTokens, storeLogout, scheduleTokenRefresh]);

  // Redirect to admin portal login
  const redirectToLogin = useCallback(() => {
    authService.redirectToAdminLogin();
  }, []);

  return {
    token,
    refreshToken,
    isAuthenticated,
    isInitialized,
    login,
    logout,
    refreshAccessToken,
    redirectToLogin,
    adminPortalUrl: authService.getAdminPortalUrl()
  };
};
